﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import Swiper from 'swiper';
import 'swiper/swiper.min.css';
import 'swiper/modules/navigation.min.css';
import 'swiper/modules/pagination.min.css';
import { Navigation, Pagination } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
import deepMerge from '@tk/utilities/tk.deep.merge';
import lightGallery from 'lightgallery';
import { LightGallery } from 'lightgallery/lightgallery';
import { LightGallerySettings } from 'lightgallery/lg-settings';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';

import 'lightgallery/scss/lightgallery.scss';
import 'lightgallery/scss/lg-thumbnail.scss';
import 'lightgallery/scss/lg-zoom.scss';

export default class TKCarousel extends TKCustomElementFactory {
    options?: string;
    customOptions: object = {};
    loadedClassName: string;
    slider?: Swiper;
    lightGalleryInstance: LightGallery | null = null;
    lightGallerySelector: string;
    lightGalleryRootSelector: string;
    lightGallerySpeed: number;
    enableLightGallery: boolean;
    lightGalleryOptions?: string;
    customLightGalleryOptions: Partial<LightGallerySettings> = {};

    constructor() {
        super();
        this.options = this.getAttribute('data-tk-carousel-options') || undefined;
        this.loadedClassName = this.getAttribute('data-tk-loaded-class-name') || 'tk-carousel--loaded';
        this.lightGallerySelector =
            this.getAttribute('data-tk-carousel-light-gallery-selector') || '[data-tk-light-gallery-item]';
        this.lightGalleryRootSelector =
            this.getAttribute('data-tk-carousel-light-gallery-root-selector') || '[data-tk-light-gallery-root]';
        this.lightGallerySpeed = Number(this.getAttribute('data-tk-carousel-light-gallery-speed')) || 500;
        this.enableLightGallery = this.hasAttribute('data-tk-carousel-light-gallery-enabled');
        this.lightGalleryOptions = this.getAttribute('data-tk-light-gallery-options') || undefined;

        if (this.options) {
            this.customOptions = JSON.parse(this.options);
        }

        if (this.lightGalleryOptions) {
            this.customLightGalleryOptions = JSON.parse(this.lightGalleryOptions);
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();

        if (this.slider) {
            this.slider.destroy(true, true);
            this.slider = undefined;
        }

        if (this.lightGalleryInstance) {
            this.lightGalleryInstance.destroy();
            this.lightGalleryInstance = null;
        }
    }

    connectedCallback(): void {
        this.mountCarousel();
    }

    mountCarousel(): void {
        const root = this.querySelector<HTMLElement>('[data-tk-carousel-root]');
        const prevButton = this.querySelector<HTMLElement>('[data-tk-carousel-prev]');
        const nextButton = this.querySelector<HTMLElement>('[data-tk-carousel-next]');
        const pagination = this.querySelector<HTMLElement>('[data-tk-carousel-pagination]');
        const rootLightGalleryItem = this.querySelector<HTMLElement>(this.lightGalleryRootSelector);

        if (!root || !prevButton || !nextButton || !pagination) {
            console.error('TKCarousel: Required elements are missing in the DOM.');
            return;
        }

        const defaultOptions: SwiperOptions = {
            modules: [Navigation, Pagination],
            slidesPerView: 1,
            spaceBetween: 24,
            navigation: {
                nextEl: nextButton,
                prevEl: prevButton,
                disabledClass: 'tk-carousel__arrow--disabled',
                enabled: false,
            },
            pagination: {
                el: pagination,
                bulletClass: 'tk-carousel__pagination-page',
                bulletActiveClass: 'tk-carousel__pagination-page--active',
                clickable: true,
                enabled: false,
            },
            breakpoints: {
                768: {
                    slidesPerView: 2,
                },
                1200: {
                    slidesPerView: 3,
                    navigation: {
                        enabled: true,
                    },
                    pagination: {
                        enabled: true,
                    },
                },
            },
            on: {
                init: this.onCarouselReady.bind(this),
            },
        };

        const config = deepMerge(defaultOptions, this.customOptions);

        this.slider = new Swiper(root, config);
        rootLightGalleryItem && this.mountLightGallery(rootLightGalleryItem);
    }

    onCarouselReady(): void {
        this.classList.add(this.loadedClassName);
    }

    private mountLightGallery(root: HTMLElement): void {
        if (!this.enableLightGallery) {
            return;
        }

        if (!root.querySelector(this.lightGallerySelector)) {
            return;
        }

        const defaultLightGalleryOptions: LightGallerySettings = {
            plugins: [lgZoom, lgThumbnail],
            speed: this.lightGallerySpeed,
            licenseKey: process.env.LIGHTGALLERY_LICENSE_KEY || '',
            selector: this.lightGallerySelector,
            thumbnail: true,
            zoom: true,
        };

        const lightGalleryConfig = deepMerge(defaultLightGalleryOptions, this.customLightGalleryOptions);

        this.lightGalleryInstance = lightGallery(root, lightGalleryConfig);
    }
}
